import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { studentFetch } from '../utils/authFetch';
import { logoutAndClearLocal } from '../api/authLogout';
import * as THREE from 'three';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const EMOJI = {
  Mathematics: '🔢', Physics: '⚛️', Chemistry: '🧪',
  Biology: '🧬', English: '📝', Arabic: '🌙',
  Science: '🔬', ICT: '💻', History: '📜',
  Geography: '🌍', Islamic: '☪️',
};

const AR_NAME = {
  Mathematics: 'الرياضيات', Physics: 'الفيزياء', Chemistry: 'الكيمياء',
  Biology: 'الأحياء', English: 'الإنجليزية', Arabic: 'العربية',
  Science: 'العلوم', ICT: 'تقنية المعلومات', History: 'التاريخ',
  Geography: 'الجغرافيا', Islamic: 'التربية الإسلامية',
  Computing: 'الحوسبة', 'Global Perspectives': 'المنظورات العالمية',
};

const T = {
  ar: {
    hello: 'أهلاً يا', settings: 'الإعدادات',
    streak: 'أيام متتالية', xp: 'نقطة', sessions: 'جلسة',
    challenge_phase1: 'تحدي خاص لك', challenge_phase2: 'ينتهي قريباً', challenge_phase3: 'يتطلب اهتماماً',
    start: 'ابدأ!', open: 'افتح',
    subjects: 'موادي', last_achievement: 'آخر إنجاز',
    last_session: 'آخر جلسة', no_subjects: 'لا توجد مواد بعد — ابدأ جلستك الأولى!',
    grade_label: 'الصف', dashboard_title: 'لوحة الأداء الأكاديمي',
    needs_review: 'مواد تحتاج مراجعة', best_day: 'أفضل يوم',
    retry: 'إعادة المحاولة', loading: 'جاري التحميل...',
    error_msg: 'تعذّر تحميل البيانات',
    nav_home: 'الرئيسية', nav_learn: 'بدء الشرح', nav_quiz: 'بدء الكويز',
    nav_results: 'النتائج', nav_flash: 'البطاقات', nav_challenges: 'التحديات', nav_achieve: 'الإنجازات',
    avg: 'متوسط الأداء', total_sessions: 'إجمالي الجلسات', no_data: 'لا توجد بيانات بعد',
    logout: 'تسجيل الخروج',
    badges: 'الشارات', challenges_done: 'تحديات مكتملة', rank: 'المرتبة',
    level: 'المستوى', xp_to_next: 'للمستوى القادم',
    quick_learn: 'تعلّم', quick_quiz: 'كويز', quick_flash: 'بطاقات',
    subjects_title: 'موادي ⭐', needs_review_badge: 'يحتاج مراجعة',
    predicted: 'التوقع', focus_count: 'تحتاج اهتماماً',
    needs_attention: '⚠️ يحتاج اهتماماً', active_challenge: 'التحدي النشط',
  },
  en: {
    hello: 'Hello,', settings: 'Settings',
    streak: 'day streak', xp: 'XP', sessions: 'sessions',
    challenge_phase1: 'Special Challenge', challenge_phase2: 'Ending Soon', challenge_phase3: 'Needs Attention',
    start: 'Start!', open: 'Open',
    subjects: 'My Subjects', last_achievement: 'Last Achievement',
    last_session: 'Last session', no_subjects: 'No subjects yet — start your first session!',
    grade_label: 'Grade', dashboard_title: 'Academic Performance',
    needs_review: 'Subjects needing review', best_day: 'Best Day',
    retry: 'Retry', loading: 'Loading...',
    error_msg: 'Failed to load data',
    nav_home: 'Home', nav_learn: 'Start Learning', nav_quiz: 'Start Quiz',
    nav_results: 'Results', nav_flash: 'Flashcards', nav_challenges: 'Challenges', nav_achieve: 'Achievements',
    avg: 'Avg. Performance', total_sessions: 'Total Sessions', no_data: 'No data yet',
    logout: 'Log Out',
    badges: 'Badges', challenges_done: 'Challenges Done', rank: 'Rank',
    level: 'Level', xp_to_next: 'to next level',
    quick_learn: 'Learn', quick_quiz: 'Quiz', quick_flash: 'Flashcards',
    subjects_title: 'My Subjects ⭐', needs_review_badge: 'Needs Review',
    predicted: 'Predicted', focus_count: 'Need Focus',
    needs_attention: '⚠️ Needs Attention', active_challenge: 'Active Challenge',
  },
};

const barColor = (p) => p >= 75 ? '#059669' : p >= 50 ? '#D97706' : '#EF4444';
const getStage = (g) => g <= 6 ? 1 : g <= 9 ? 2 : 3;
const emojiFor = (name) => {
  if (!name) return '📘';
  if (EMOJI[name]) return EMOJI[name];
  const n = name.toLowerCase();
  if (n.includes('math') || n.includes('رياض')) return '🔢';
  if (n.includes('physics') || n.includes('فيزي')) return '⚛️';
  if (n.includes('chem') || n.includes('كيمي')) return '🧪';
  if (n.includes('bio') || n.includes('أحيا') || n.includes('احيا')) return '🧬';
  if (n.includes('english')) return '📝';
  if (n.includes('arabic') || n.includes('عرب') || n.includes('اللغة')) return '🌙';
  if (n.includes('science') || n.includes('علوم')) return '🔬';
  if (n.includes('ict') || n.includes('تقني') || n.includes('computer') || n.includes('computing')) return '💻';
  if (n.includes('history') || n.includes('تاريخ')) return '📜';
  if (n.includes('geo') || n.includes('جغراف')) return '🌍';
  if (n.includes('islamic') || n.includes('إسلام') || n.includes('اسلام') || n.includes('تربية')) return '☪️';
  if (n.includes('global') || n.includes('perspective')) return '🌐';
  if (n.includes('qatar') || n.includes('قطر') || n.includes('مواطن')) return '🇶🇦';
  if (n.includes('french') || n.includes('فرنس')) return '🇫🇷';
  if (n.includes('art') || n.includes('فنون')) return '🎨';
  if (n.includes('music') || n.includes('موسيق')) return '🎵';
  if (n.includes('sport') || n.includes('رياضة') || n.includes('تربية البدنية')) return '⚽';
  return '📘';
};
const subjectLabel = (name, lang) => lang === 'ar' ? (AR_NAME[name] || name) : name;
const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d) ? null : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;700;800;900&family=Nunito:wght@400;700;800;900&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .sd-root {
    min-height: 100vh;
    background: transparent;
    font-family: 'Nunito', sans-serif;
    display: flex;
  }

  .sd-sidebar {
    position: fixed; top: 0; bottom: 0; width: 220px;
    background: rgba(255,255,255,.82);
    backdrop-filter: blur(32px) saturate(2.2);
    -webkit-backdrop-filter: blur(32px) saturate(2.2);
    border: 1px solid #E5E7EB;
    display: flex; flex-direction: column;
    z-index: 200; padding: 0;
    transition: transform .25s ease;
  }
  .sd-sidebar-logo {
    padding: 22px 20px 16px;
    font-family: 'Sora', sans-serif; font-size: 1.5rem; font-weight: 800;
    color: #1E3A5F; border-bottom: 1px solid #F3F4F6;
  }
  .sd-sidebar-logo span { color: #059669; }
  .sd-nav { flex: 1; overflow-y: auto; padding: 10px 0; }
  .sd-nav-item {
    display: flex; align-items: center; gap: 10px;
    padding: 11px 20px; cursor: pointer;
    font-size: .92rem; font-weight: 800; color: #6B7280;
    transition: all .15s ease; white-space: nowrap;
    border: none; background: none; width: 100%; text-align: inherit;
  }
  .sd-nav-item:hover { background: #F9FAFB; color: #059669; }
  .sd-nav-item.active {
    background: #F0FDF4; color: #059669; font-weight: 800;
    border-right: 3px solid #059669;
  }
  .sd-nav-item .icon { font-size: 1.1rem; flex-shrink: 0; }
  .sd-sidebar-footer {
    padding: 14px 20px; border-top: 1px solid #F3F4F6;
  }
  .sd-logout-btn {
    width: 100%; padding: 9px 14px; border-radius: 10px;
    border: 1.5px solid #E5E7EB; background: white;
    color: #6B7280; font-family: 'Nunito', sans-serif;
    font-size: .85rem; font-weight: 700; cursor: pointer;
    transition: all .2s;
  }
  .sd-logout-btn:hover { border-color: #EF4444; color: #EF4444; background: #FEF2F2; }

  .sd-xp-bar {
    height: 6px; background: #F3F4F6; border-radius: 20px;
    overflow: hidden; margin-top: 5px;
    box-shadow: inset 0 1px 2px rgba(0,0,0,.08);
  }
  .sd-xp-fill {
    height: 100%; border-radius: 20px;
    background: linear-gradient(90deg,#059669,#34D399);
    position: relative; overflow: hidden;
  }
  @keyframes sd-shimmer {
    from { left: -100%; }
    to   { left: 200%; }
  }
  .sd-xp-fill::after {
    content: ''; position: absolute; top: 0; left: -100%;
    width: 40%; height: 100%;
    background: linear-gradient(90deg,transparent,rgba(255,255,255,.5),transparent);
    animation: sd-shimmer 2s infinite;
  }

  .sd-main {
    flex: 1; padding: 28px 24px; overflow-y: auto;
    min-height: 100vh; background: transparent;
  }

  .sd-card {
    background: rgba(255,255,255,.72);
    backdrop-filter: blur(32px) saturate(2.2);
    -webkit-backdrop-filter: blur(32px) saturate(2.2);
    border: 1px solid rgba(255,255,255,.92);
    border-radius: 18px; padding: 20px;
    box-shadow: 0 8px 48px rgba(0,0,0,.07), 0 1px 0 rgba(255,255,255,.98) inset;
    transform-style: preserve-3d;
    transition: transform .3s cubic-bezier(.34,1.56,.64,1), box-shadow .3s;
    cursor: pointer;
    position: relative;
  }
  .sd-card:hover { box-shadow: 0 6px 28px rgba(5,150,105,.13); }
  .sd-card::before, .sd-subject-card-grid::before {
    content:''; position:absolute; inset:0; border-radius:inherit;
    background:radial-gradient(circle at var(--lx,50%) var(--ly,30%),rgba(255,255,255,.38) 0%,transparent 60%);
    pointer-events:none; z-index:1; opacity:0; transition:opacity .25s;
  }
  .sd-card.tilting::before, .sd-subject-card-grid.tilting::before { opacity:1; }

  .sd-header-emerald {
    background: linear-gradient(135deg,#059669 0%,#10B981 100%);
    border-radius: 22px; padding: 22px 20px;
    color: white; margin-bottom: 18px;
    display: flex; align-items: center; justify-content: space-between;
    box-shadow: 0 6px 0 #0F2033, 0 12px 32px rgba(30,58,95,.25);
  }
  .sd-header-navy {
    background: linear-gradient(135deg,#1E3A5F,#2D5A9E);
    border-radius: 22px; padding: 20px;
    box-shadow: 0 6px 0 #0F2033, 0 12px 32px rgba(30,58,95,.25);
    color: white; margin-bottom: 16px;
  }
  .sd-header-white {
    background: rgba(255,255,255,.72);
    backdrop-filter: blur(32px) saturate(2.2);
    -webkit-backdrop-filter: blur(32px) saturate(2.2);
    border: 1px solid rgba(255,255,255,.92);
    border-radius: 18px; padding: 20px;
    margin-bottom: 18px; box-shadow: 0 4px 28px rgba(0,0,0,.06), 0 1px 0 rgba(255,255,255,.98) inset;
    display: flex; align-items: center; justify-content: space-between;
  }
  .sd-header-academic {
    background: rgba(255,255,255,.72);
    backdrop-filter: blur(32px) saturate(2.2);
    -webkit-backdrop-filter: blur(32px) saturate(2.2);
    border: 1px solid rgba(255,255,255,.92);
    border-radius: 18px; padding: 20px 24px;
    margin-bottom: 18px; box-shadow: 0 4px 28px rgba(0,0,0,.06), 0 1px 0 rgba(255,255,255,.98) inset;
  }

  .sd-avatar {
    border-radius: 50%; object-fit: cover;
    border: 3px solid rgba(255,255,255,.4);
    flex-shrink: 0;
  }
  .sd-avatar-placeholder {
    border-radius: 18px; background: rgba(255,255,255,.18);
    display: flex; align-items: center; justify-content: center;
    font-size: 1.4rem; flex-shrink: 0;
    width: 52px; height: 52px;
    border: 2px solid rgba(255,255,255,.3);
    box-shadow: 0 4px 12px rgba(0,0,0,.2);
  }
  .sd-hello {
    font-family: 'Sora', sans-serif; font-size: 1.25rem;
    font-weight: 800; line-height: 1.2;
  }
  .sd-grade-tag {
    font-size: .78rem; font-weight: 600;
    background: rgba(255,255,255,.2); border-radius: 20px;
    padding: 3px 10px; display: inline-block; margin-top: 4px;
  }
  .sd-settings-btn {
    background: rgba(255,255,255,.18); border: 2px solid rgba(255,255,255,.3);
    border-radius: 14px; padding: 8px 12px;
    color: white; cursor: pointer; font-size: .82rem;
    font-family: 'Nunito', sans-serif; font-weight: 700;
    transition: transform .08s, box-shadow .08s, background .2s;
    white-space: nowrap; box-shadow: 0 2px 0 rgba(0,0,0,.1);
  }
  .sd-settings-btn:hover { background: rgba(255,255,255,.3); }
  .sd-settings-btn:active { transform: translateY(2px); }
  .sd-settings-btn-dark {
    background: #F9FAFB; border: 2px solid #E5E7EB;
    border-radius: 14px; padding: 7px 14px;
    color: #4B5563; cursor: pointer; font-size: .82rem;
    font-family: 'Nunito', sans-serif; font-weight: 700;
    transition: transform .08s, box-shadow .08s, border-color .2s, color .2s;
    box-shadow: 0 3px 0 #E5E7EB;
  }
  .sd-settings-btn-dark:hover { border-color: #059669; color: #059669; }
  .sd-settings-btn-dark:active { transform: translateY(2px); }

  .sd-stats-row {
    display: grid; grid-template-columns: repeat(3,1fr);
    gap: 12px; margin-bottom: 18px;
  }
  .sd-stat-card {
    background: rgba(255,255,255,.72);
    backdrop-filter: blur(28px) saturate(2);
    -webkit-backdrop-filter: blur(28px) saturate(2);
    border-radius: 20px; padding: 14px 12px;
    text-align: center;
    border: 2px solid transparent;
    position: relative; overflow: hidden;
  }
  .sd-stat-glow {
    position:absolute; inset:0; border-radius:inherit; pointer-events:none; z-index:0;
    background:radial-gradient(circle at 50% 0%, rgba(5,150,105,.14), transparent 70%);
    opacity:0; transition:opacity .35s;
  }
  .sd-stat-card:hover .sd-stat-glow { opacity:1; }
  .sd-stat-amber { background: rgba(254,243,199,.55); border-color: #FDE68A; box-shadow: 0 5px 0 #FDE68A; }
  .sd-stat-green { background: rgba(209,250,229,.55); border-color: #A7F3D0; box-shadow: 0 5px 0 #A7F3D0; }
  .sd-stat-blue  { background: rgba(219,234,254,.55); border-color: #BFDBFE; box-shadow: 0 5px 0 #BFDBFE; }
  .sd-stat-purple{ background: rgba(237,233,254,.55); border-color: #DDD6FE; box-shadow: 0 5px 0 #DDD6FE; }
  .sd-stat-red   { background: rgba(254,226,226,.55); border-color: #FECACA; box-shadow: 0 5px 0 #FECACA; }
  .sd-stat-ico {
    font-size: 32px;
    margin-bottom: 12px;
    display: block;
    transition: .45s cubic-bezier(.34,1.56,.64,1);
    filter: drop-shadow(0 4px 8px rgba(0,0,0,.1));
  }
  .sd-stat-card:hover .sd-stat-ico {
    transform: scale(1.25) translateY(-4px) rotate(-8deg);
    filter: drop-shadow(0 8px 18px rgba(0,0,0,.15));
  }
  .sd-stat-val {
    font-family: 'Sora', sans-serif;
    font-size: 28px;
    font-weight: 800;
    color: #1E3A5F;
    line-height: 1;
  }
  .sd-stat-lbl {
    font-size: 11px;
    color: #9CA3AF;
    font-weight: 700;
    margin-top: 5px;
    text-transform: uppercase;
    letter-spacing: .06em;
  }

  .sd-challenge-card {
    border-radius: 14px; padding: 16px; margin-bottom: 18px;
  }
  .sd-challenge-label {
    font-size: .7rem; font-weight: 800; letter-spacing: .1em;
    text-transform: uppercase; margin-bottom: 6px;
  }
  .sd-challenge-text {
    font-family: 'Sora', sans-serif; font-size: .95rem;
    font-weight: 700; color: #1F2937; margin-bottom: 12px;
  }
  .sd-btn {
    border: 2px solid transparent; border-radius: 18px; padding: 9px 18px;
    font-family: 'Sora', sans-serif; font-size: .85rem;
    font-weight: 900; cursor: pointer;
    transition: transform .08s, box-shadow .08s;
  }
  .sd-btn:active { transform: translateY(4px); }
  .sd-btn-green {
    background: linear-gradient(135deg,#059669,#10B981); color: white;
    box-shadow: 0 3px 10px rgba(5,150,105,.3);
  }
  .sd-btn-green:hover { transform: translateY(-1px); box-shadow: 0 5px 16px rgba(5,150,105,.4); }
  .sd-btn-gold {
    background: linear-gradient(135deg,#D97706,#F59E0B); color: white;
    box-shadow: 0 3px 10px rgba(217,119,6,.3);
  }
  .sd-btn-gold:hover { transform: translateY(-1px); }
  .sd-btn-learn {
    background: linear-gradient(135deg,#047857,#059669 45%,#10B981 80%,#34D399);
    color: #fff;
    border: none;
    box-shadow: 0 8px 28px rgba(5,150,105,.4);
    position: relative;
    overflow: hidden;
    font-family: 'Sora', sans-serif;
    font-weight: 800;
    font-size: 14px;
  }
  .sd-btn-learn:hover {
    transform: translateY(-4px) scale(1.01);
    box-shadow: 0 16px 44px rgba(5,150,105,.52);
    color: #fff;
  }
  .sd-btn-learn:active { transform: translateY(1px); }
  .sd-btn-learn::before {
    content: '';
    position: absolute;
    width: 180px; height: 180px;
    border-radius: 50%;
    background: rgba(255,255,255,.07);
    top: -70px; right: -50px;
    pointer-events: none;
  }
  .sd-btn-learn-ring {
    position: absolute;
    left: 18px; top: 50%;
    transform: translateY(-50%);
    width: 28px; height: 28px;
    border-radius: 50%;
    border: 2px solid rgba(255,255,255,.25);
    animation: sd-ring-spin 9s linear infinite;
    pointer-events: none;
  }
  .sd-btn-learn-ring::after {
    content: '';
    position: absolute;
    top: -3px; left: 50%;
    width: 5px; height: 5px;
    border-radius: 50%;
    background: rgba(255,255,255,.6);
    transform: translateX(-50%);
  }
  @keyframes sd-ring-spin {
    to { transform: translateY(-50%) rotate(360deg); }
  }
  .sd-btn-quiz {
    background: #DBEAFE; color: #1E3A5F;
    border-color: #93C5FD;
    box-shadow: 0 5px 0 #93C5FD, 0 8px 16px rgba(37,99,235,.12);
  }
  .sd-btn-quiz:active { box-shadow: 0 1px 0 #93C5FD; }
  .sd-btn-flash {
    background: #FEF3C7; color: #92400E;
    border-color: #FDE68A;
    box-shadow: 0 5px 0 #FDE68A, 0 8px 16px rgba(217,119,6,.12);
  }
  .sd-btn-flash:active { box-shadow: 0 1px 0 #FDE68A; }

  .sd-section-title {
    font-family: 'Sora', sans-serif; font-size: .85rem;
    font-weight: 800; color: #9CA3AF; letter-spacing: .04em;
    text-transform: uppercase; margin-bottom: 12px;
  }

  .sd-subjects-grid {
    display: grid; grid-template-columns: repeat(2,1fr); gap: 12px;
    margin-bottom: 18px;
  }
  .sd-subject-card-grid {
    background: rgba(255,255,255,.72);
    backdrop-filter: blur(32px) saturate(2.2);
    -webkit-backdrop-filter: blur(32px) saturate(2.2);
    border: 1px solid rgba(255,255,255,.92);
    border-radius: 22px; padding: 14px 12px;
    transform-style: preserve-3d;
    position: relative;
    transition: transform .3s cubic-bezier(.34,1.56,.64,1), box-shadow .3s;
    box-shadow: 0 8px 40px rgba(0,0,0,.07), 0 1px 0 rgba(255,255,255,.98) inset;
  }
  .sd-subject-card-grid:hover {
    border-color: #D1D5DB; box-shadow: 0 5px 0 #D1D5DB, 0 18px 36px rgba(0,0,0,.1);
    transform: translateY(-6px);
  }
  .sd-subject-card-grid:active {
    transform: translateY(4px);
    box-shadow: 0 1px 0 #E5E7EB;
  }
  .sd-subject-arrow {
    font-size:16px; opacity:0; transform:translateX(-10px);
    transition:.3s cubic-bezier(.34,1.56,.64,1); font-weight:700; color:#059669;
    display:inline-block; margin-top:8px;
  }
  .sd-subject-card-grid:hover .sd-subject-arrow { opacity:1; transform:translateX(0); }
  .sd-subject-shine {
    position: absolute; inset: 0;
    background: linear-gradient(135deg, rgba(255,255,255,.55), transparent 52%);
    border-radius: inherit;
    pointer-events: none;
    z-index: 1;
  }
  .sd-subject-ico-bg {
    width: 56px; height: 56px; border-radius: 18px;
    display: flex; align-items: center; justify-content: center;
    margin-bottom: 14px; flex-shrink: 0;
  }
  .sd-subject-emoji {
    font-size: 40px;
    display: block;
    filter: drop-shadow(0 6px 12px rgba(0,0,0,.12));
    transition: .45s cubic-bezier(.34,1.56,.64,1);
    background: none;
    border-radius: 0;
    width: auto;
    height: auto;
    padding: 0;
    line-height: 1;
  }
  .sd-subject-card-grid:hover .sd-subject-emoji {
    transform: scale(1.24) translateY(-5px) rotate(-7deg);
    filter: drop-shadow(0 10px 20px rgba(0,0,0,.18));
  }
  .sd-subj-math    { background: linear-gradient(145deg,#818CF8,#6366F1); }
  .sd-subj-science { background: linear-gradient(145deg,#34D399,#10B981); }
  .sd-subj-english { background: linear-gradient(145deg,#60A5FA,#3B82F6); }
  .sd-subj-arabic  { background: linear-gradient(145deg,#FBBF24,#D97706); }
  .sd-subj-ict     { background: linear-gradient(145deg,#C084FC,#9333EA); }
  .sd-subj-physics { background: linear-gradient(145deg,#67E8F9,#0891B2); }
  .sd-subj-chem    { background: linear-gradient(145deg,#86EFAC,#16A34A); }
  .sd-subj-bio     { background: linear-gradient(145deg,#6EE7B7,#059669); }
  .sd-subj-islamic { background: linear-gradient(145deg,#FCD34D,#D97706); }
  .sd-subj-history { background: linear-gradient(145deg,#FCA5A5,#DC2626); }
  .sd-subj-geo     { background: linear-gradient(145deg,#93C5FD,#2563EB); }
  .sd-subj-default { background: linear-gradient(145deg,#E5E7EB,#9CA3AF); }
  .sd-subject-name {
    font-family: 'Sora', sans-serif;
    font-size: 15px;
    font-weight: 800;
    color: #1E3A5F;
    margin-bottom: 3px;
  }

  .sd-progress-track {
    height: 5px; background: rgba(0,0,0,.07); border-radius: 5px; overflow: hidden;
    margin-bottom: 6px;
    box-shadow: inset 0 1px 2px rgba(0,0,0,.08);
  }
  .sd-progress-fill {
    height: 100%; border-radius: 5px;
    transition: width 1.4s cubic-bezier(.34,1.56,.64,1);
    position: relative; overflow: hidden;
  }
  .sd-progress-fill::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,.45), transparent);
    animation: sd-shine 2.8s ease-in-out infinite;
  }
  @keyframes sd-shine {
    0%   { transform: translateX(-100%); }
    100% { transform: translateX(200%); }
  }
  .sd-progress-pct {
    font-size: .75rem; font-weight: 700; color: #6B7280;
  }

  .sd-subjects-list { display: flex; flex-direction: column; gap: 10px; margin-bottom: 18px; }
  .sd-subject-row {
    background: rgba(255,255,255,.72);
    backdrop-filter: blur(28px) saturate(2);
    -webkit-backdrop-filter: blur(28px) saturate(2);
    border: 1px solid rgba(255,255,255,.9);
    border-radius: 18px; padding: 14px 16px;
    display: flex; align-items: center; gap: 12px;
    transition: all .2s;
    box-shadow: 0 4px 24px rgba(0,0,0,.05);
  }
  .sd-subject-row:hover { border-color: #D1D5DB; box-shadow: 0 3px 12px rgba(5,150,105,.12); transform: translateY(-3px); }
  .sd-subject-row-info { flex: 1; min-width: 0; }
  .sd-subject-row-name {
    font-family: 'Sora', sans-serif; font-size: .92rem;
    font-weight: 700; color: #064E3B; margin-bottom: 6px;
  }
  .sd-subject-row-meta {
    display: flex; gap: 10px; align-items: center;
    font-size: .75rem; color: #6B7280; font-weight: 600;
  }
  .sd-subject-row-pct {
    font-family: 'Sora', sans-serif; font-size: 1.1rem;
    font-weight: 800; flex-shrink: 0; min-width: 44px; text-align: center;
  }

  .sd-stat-grid-2x2 {
    display: grid; grid-template-columns: repeat(2,1fr);
    gap: 12px; margin-bottom: 18px;
  }
  .sd-stat-box {
    background: white; border: 1px solid #E5E7EB;
    border-radius: 14px; padding: 16px;
    box-shadow: 0 2px 8px rgba(5,150,105,.06);
  }
  .sd-stat-box-val {
    font-family: 'Sora', sans-serif; font-size: 1.3rem;
    font-weight: 800; color: #064E3B;
  }
  .sd-stat-box-lbl { font-size: .78rem; font-weight: 600; color: #6B7280; margin-top: 3px; }

  .sd-achieve-card {
    background: #F0FDF4; border: 1px solid #10B981;
    border-radius: 14px; padding: 14px 16px;
    display: flex; align-items: center; gap: 12px;
    margin-bottom: 18px;
  }
  .sd-achieve-icon { font-size: 2rem; }
  .sd-achieve-name { font-family: 'Sora', sans-serif; font-size: .9rem; font-weight: 700; color: #064E3B; }
  .sd-achieve-label { font-size: .72rem; color: #059669; font-weight: 700; margin-bottom: 2px; }

  .sd-empty {
    text-align: center; padding: 32px 16px;
    color: #9CA3AF; font-size: .9rem; font-weight: 600;
  }
  .sd-empty-icon { font-size: 2.5rem; margin-bottom: 8px; }

  @keyframes sd-pulse {
    0%,100% { opacity: 1; } 50% { opacity: .45; }
  }
  .sd-skeleton {
    background: #E5E7EB; border-radius: 10px;
    animation: sd-pulse 1.4s ease-in-out infinite;
  }

  .sd-bottom-nav {
    display: none; position: fixed; bottom: 0; left: 0; right: 0;
    background: white; border-top: 2px solid #F3F4F6;
    z-index: 200; padding: 6px 0 env(safe-area-inset-bottom,6px);
  }
  .sd-bottom-nav-inner {
    display: flex; justify-content: space-around; align-items: center;
  }
  .sd-bottom-item {
    display: flex; flex-direction: column; align-items: center; gap: 2px;
    padding: 6px 10px; cursor: pointer; font-size: .62rem;
    font-weight: 700; color: #6B7280; border: none; background: none;
    transition: color .15s;
  }
  .sd-bottom-item.active { color: #059669; }
  .sd-bottom-item.active::after {
    content: ''; display: block;
    width: 4px; height: 4px; border-radius: 50%;
    background: #059669; margin: 2px auto 0;
  }
  .sd-bottom-item .bi { font-size: 1.3rem; }

  @media (max-width: 767px) {
    .sd-sidebar { display: none; }
    .sd-bottom-nav { display: block; }
    .sd-main { padding: 16px 14px 90px !important; }
    .sd-subjects-grid { grid-template-columns: repeat(2,1fr); gap: 8px; }
    .sd-stat-grid-2x2 { grid-template-columns: 1fr; }
  }
  @media (min-width: 768px) and (max-width: 1023px) {
    .sd-sidebar { display: flex; width: 200px; }
    .sd-bottom-nav { display: none; }
    .sd-main { margin-left: 200px; }
    .sd-subjects-grid { grid-template-columns: repeat(2,1fr); }
  }
  @media (min-width: 1024px) {
    .sd-sidebar { display: flex; width: 220px; }
    .sd-bottom-nav { display: none; }
    .sd-subjects-grid { grid-template-columns: repeat(4,1fr); }
  }

  [data-anim]{opacity:0;will-change:transform,opacity}
  [data-anim="fadeUp"]   {transform:translateY(36px)}
  [data-anim="fadeLeft"] {transform:translateX(-36px)}
  [data-anim="fadeRight"]{transform:translateX(36px)}
  [data-anim="scaleIn"]  {transform:scale(.88)}
  [data-anim="flipIn"]   {transform:perspective(600px) rotateX(20deg) translateY(28px)}
  [data-anim].in{
    opacity:1!important;transform:none!important;
    transition:opacity .65s cubic-bezier(.22,1,.36,1),transform .65s cubic-bezier(.22,1,.36,1);
  }
`;

function ProgressBar({ pct }) {
  const p = Math.min(100, Math.max(0, pct || 0));
  return (
    <div className="sd-progress-track">
      <div className="sd-progress-fill" style={{ width: `${p}%`, background: barColor(p) }} />
    </div>
  );
}

function ChallengeCard({ challenge, lang, t, onStart }) {
  if (!challenge) return null;
  const { challenge_status, weakness_name, action_text } = challenge;
  const cfg = {
    phase1: { bg: '#EFF6FF', border: '#3B82F6', labelColor: '#2563EB', label: t.challenge_phase1, btnLabel: t.start, btnCls: 'sd-btn sd-btn-green' },
    phase2: { bg: '#FEF9EE', border: '#D97706', labelColor: '#D97706', label: t.challenge_phase2, btnLabel: t.open, btnCls: 'sd-btn sd-btn-gold' },
    phase3: { bg: '#FEF2F2', border: '#EF4444', labelColor: '#EF4444', label: t.challenge_phase3, btnLabel: null, btnCls: null },
  }[challenge_status] || null;
  if (!cfg) return null;
  return (
    <div className="sd-challenge-card" style={{ background: cfg.bg, border: `2px solid ${cfg.border}` }}>
      <div className="sd-challenge-label" style={{ color: cfg.labelColor }}>{cfg.label}</div>
      <div className="sd-challenge-text">{action_text || weakness_name}</div>
      {cfg.btnLabel && (
        <button className={cfg.btnCls} onClick={onStart}>
          {cfg.btnLabel}
        </button>
      )}
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div>
      <div className="sd-skeleton" style={{ height: 90, marginBottom: 16 }} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 16 }}>
        {[0,1,2].map(i => <div key={i} className="sd-skeleton" style={{ height: 72 }} />)}
      </div>
      <div className="sd-skeleton" style={{ height: 64, marginBottom: 16 }} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12 }}>
        {[0,1,2,3].map(i => <div key={i} className="sd-skeleton" style={{ height: 110 }} />)}
      </div>
    </div>
  );
}

function subjectClass(name) {
  const n = (name || '').toLowerCase();
  if (n.includes('math') || n.includes('رياض')) return 'sd-subj-math';
  if (n.includes('science') || n.includes('علوم')) return 'sd-subj-science';
  if (n.includes('english')) return 'sd-subj-english';
  if (n.includes('arabic') || n.includes('عرب') || n.includes('اللغة')) return 'sd-subj-arabic';
  if (n.includes('ict') || n.includes('تقني') || n.includes('computer') || n.includes('computing')) return 'sd-subj-ict';
  if (n.includes('physics') || n.includes('فيزي')) return 'sd-subj-physics';
  if (n.includes('chem') || n.includes('كيمي')) return 'sd-subj-chem';
  if (n.includes('bio') || n.includes('أحيا') || n.includes('احيا')) return 'sd-subj-bio';
  if (n.includes('islamic') || n.includes('إسلام') || n.includes('اسلام') || n.includes('تربية')) return 'sd-subj-islamic';
  if (n.includes('history') || n.includes('تاريخ') || n.includes('qatar') || n.includes('قطر') || n.includes('مواطن')) return 'sd-subj-history';
  if (n.includes('geo') || n.includes('جغراف')) return 'sd-subj-geo';
  if (n.includes('global') || n.includes('perspective')) return 'sd-subj-english';
  return 'sd-subj-default';
}

function Stage1({ data, lang, t, activeChallenges, challengesDone }) {
  const { student, stats, subjects } = data;
  const streak = student.streak_days || stats.current_streak || 0;
  const totalXp = student.total_xp || stats.total_xp || 0;
  const badges = student.badges_count || 0;
  const level = student.level || 1;
  const xpToNext = student.xp_to_next_level || 500;
  const xpPct = Math.min(100, Math.round(((totalXp % xpToNext) / xpToNext) * 100));

  return (
    <>
      {/* Welcome card */}
      <div data-anim="flipIn" data-delay="0" style={{ background: 'linear-gradient(135deg,#059669,#10B981)', borderRadius: 22, padding: '20px', marginBottom: 16, boxShadow: '0 6px 0 #0B2240, 0 12px 32px rgba(5,150,105,.25)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {student.avatar_url
              ? <img src={student.avatar_url} alt="" className="sd-avatar" style={{ width: 54, height: 54 }} />
              : <div className="sd-avatar-placeholder" style={{ color: 'white', fontFamily: 'Sora, sans-serif', fontWeight: 800 }}>
                  {(student.name || student.first_name || '')[0]}
                </div>
            }
            <div>
              <div style={{ fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: 34, color: 'white' }}>{t.hello} {student.first_name} ✨</div>
              <div style={{ fontSize: 14, fontWeight: 600, background: 'rgba(255,255,255,.2)', borderRadius: 20, padding: '2px 10px', display: 'inline-block', marginTop: 4, color: 'rgba(255,255,255,.75)' }}>
                {t.grade_label} {student.grade} · {t.level} {level}
              </div>
            </div>
          </div>
          <button className="sd-settings-btn" onClick={() => window.location.href = '/student/settings'}>{t.settings}</button>
        </div>
        <div style={{ fontSize: '.75rem', color: 'rgba(255,255,255,.8)', fontWeight: 600, marginBottom: 5 }}>
          ⭐ {totalXp} {t.xp} · {t.xp_to_next}
        </div>
        <div className="sd-xp-bar" style={{ marginTop: 8 }}>
          <div className="sd-xp-fill" style={{ width: `${xpPct}%` }} />
        </div>
      </div>

      {/* Stats row: streak, badges, challenges */}
      <div className="sd-stats-row" data-anim="fadeUp" data-delay="80">
        <div className="sd-stat-card sd-stat-amber">
          <div className="sd-stat-glow" />
          <span className="sd-stat-ico">🔥</span>
          <div className="sd-stat-val" style={{ color: '#D97706' }}>{streak}</div>
          <div className="sd-stat-lbl">{t.streak}</div>
        </div>
        <div className="sd-stat-card sd-stat-green">
          <div className="sd-stat-glow" />
          <span className="sd-stat-ico">🏅</span>
          <div className="sd-stat-val" style={{ color: '#059669' }}>{badges}</div>
          <div className="sd-stat-lbl">{t.badges}</div>
        </div>
        <div className="sd-stat-card sd-stat-purple">
          <div className="sd-stat-glow" />
          <span className="sd-stat-ico">⚡</span>
          <div className="sd-stat-val" style={{ color: '#7C3AED' }}>{challengesDone}</div>
          <div className="sd-stat-lbl">{t.challenges_done}</div>
        </div>
      </div>

      {/* Quick actions */}
      <div data-anim="fadeUp" data-delay="140" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 18 }}>
        <button className="sd-btn sd-btn-learn" style={{ padding: '10px 8px', fontSize: '.82rem' }} onClick={() => window.location.href = '/student/subjects'}><span className="sd-btn-learn-ring" />📚 {t.quick_learn}</button>
        <button className="sd-btn sd-btn-quiz" style={{ padding: '10px 8px', fontSize: '.82rem' }} onClick={() => window.location.href = '/student/quizzes'}>❓ {t.quick_quiz}</button>
        <button className="sd-btn sd-btn-flash" style={{ padding: '10px 8px', fontSize: '.82rem' }} onClick={() => window.location.href = '/student/flashcards'}>🃏 {t.quick_flash}</button>
      </div>

      {activeChallenges.length > 0 && (
        <ChallengeCard
          challenge={activeChallenges[0]}
          lang={lang}
          t={t}
          onStart={() => {
            const ch = activeChallenges[0];
            if (!ch) return;
            const sd = JSON.parse(localStorage.getItem('student_data') || '{}');
            if (ch.unit_id && ch.unit_name && ch.subject_name) {
              window.location.href =
                `/student/subject-units?` +
                `subject_id=${ch.subject_id}` +
                `&subject=${encodeURIComponent(ch.subject_name)}` +
                `&unit_id=${ch.unit_id}` +
                `&unit=${encodeURIComponent(ch.unit_name)}` +
                `&topic=${encodeURIComponent(ch.weakness_name || '')}`;
            } else {
              window.location.href = '/student/subjects';
            }
          }}
        />
      )}

      {/* Subjects grid */}
      <div className="sd-section-title">{t.subjects_title}</div>
      {subjects.length === 0
        ? <div className="sd-empty"><div className="sd-empty-icon">📚</div>{t.no_subjects}</div>
        : <div className="sd-subjects-grid">
            {subjects.map((s, i) => {
              const pct = Math.round(s.progress_pct ?? s.avg_correct_percentage ?? 0);
              const stars = Math.min(5, Math.round(pct / 20));
              const name = s.name || s.subject_name || '';
              return (
                <div key={s.subject_id || i} className="sd-subject-card-grid"
                  data-anim="scaleIn" data-delay={i * 60}
                  onMouseMove={e=>{const r=e.currentTarget.getBoundingClientRect();const x=(e.clientX-r.left)/r.width*100;const y=(e.clientY-r.top)/r.height*100;e.currentTarget.style.setProperty('--lx',x+'%');e.currentTarget.style.setProperty('--ly',y+'%');const rx=(e.clientY-r.top-r.height/2)/(r.height/2)*8;const ry=-(e.clientX-r.left-r.width/2)/(r.width/2)*8;e.currentTarget.style.transform=`perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg) translateZ(6px)`;e.currentTarget.classList.add('tilting');}}
                  onMouseLeave={e=>{e.currentTarget.style.transform='';e.currentTarget.classList.remove('tilting');}}
                >
                  <div className="sd-subject-shine" />
                  <div className={`sd-subject-ico-bg ${subjectClass(name)}`}>
                    <span className="sd-subject-emoji">{emojiFor(name)}</span>
                  </div>
                  <div className="sd-subject-name">{subjectLabel(name, lang)}</div>
                  <ProgressBar pct={pct} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                    <span className="sd-progress-pct">{pct}%</span>
                    <span style={{ fontSize: '.78rem', color: '#D97706', letterSpacing: 1 }}>{'⭐'.repeat(stars)}</span>
                  </div>
                  <div className="sd-subject-arrow">{lang === 'ar' ? '←' : '→'}</div>
                </div>
              );
            })}
          </div>
      }
    </>
  );
}

function Stage2({ data, lang, t, activeChallenges, challengesDone }) {
  const { student, stats, subjects } = data;
  const streak = student.streak_days || stats.current_streak || 0;
  const totalXp = student.total_xp || stats.total_xp || 0;
  const avgPerf = stats.avg_performance != null
    ? Math.round(stats.avg_performance)
    : Math.round(subjects.length ? subjects.reduce((a, s) => a + (s.progress_pct ?? s.avg_correct_percentage ?? 0), 0) / subjects.length : 0);
  const rank = student.rank || '—';

  return (
    <>
      {/* Welcome card - navy gradient */}
      <div className="sd-header-navy" data-anim="flipIn" data-delay="0">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {student.avatar_url
              ? <img src={student.avatar_url} alt="" className="sd-avatar" style={{ width: 52, height: 52 }} />
              : <div className="sd-avatar-placeholder" style={{ color: 'white', fontFamily: 'Sora, sans-serif', fontWeight: 800 }}>
                  {(student.name || student.first_name || '')[0]}
                </div>
            }
            <div>
              <div style={{ fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: 34, color: 'white' }}>{t.hello} {student.first_name}</div>
              <div style={{ fontSize: 14, color: 'rgba(255,255,255,.75)', fontWeight: 600, marginTop: 3 }}>{t.grade_label} {student.grade}</div>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
            <button className="sd-settings-btn" onClick={() => window.location.href = '/student/settings'}>⚙️ {t.settings}</button>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: 'Sora,sans-serif', fontSize: '1.4rem', fontWeight: 800, color: '#F59E0B' }}>⭐ {totalXp}</div>
              <div style={{ fontSize: '.72rem', color: 'rgba(255,255,255,.6)', fontWeight: 600 }}>{t.xp}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats row: streak, avg performance, rank */}
      <div className="sd-stats-row" data-anim="fadeUp" data-delay="80">
        <div className="sd-stat-card sd-stat-amber">
          <div className="sd-stat-glow" />
          <span className="sd-stat-ico">🔥</span>
          <div className="sd-stat-val" style={{ color: '#D97706' }}>{streak}</div>
          <div className="sd-stat-lbl">{t.streak}</div>
        </div>
        <div className="sd-stat-card sd-stat-green">
          <div className="sd-stat-glow" />
          <span className="sd-stat-ico">📊</span>
          <div className="sd-stat-val" style={{ color: barColor(avgPerf) }}>{avgPerf}%</div>
          <div className="sd-stat-lbl">{t.avg}</div>
        </div>
        <div className="sd-stat-card sd-stat-blue">
          <div className="sd-stat-glow" />
          <span className="sd-stat-ico">🏆</span>
          <div className="sd-stat-val" style={{ color: '#1E3A5F' }}>#{rank}</div>
          <div className="sd-stat-lbl">{t.rank}</div>
        </div>
      </div>

      {/* Quick actions */}
      <div data-anim="fadeUp" data-delay="140" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 18 }}>
        <button className="sd-btn sd-btn-learn" style={{ padding: '10px 8px', fontSize: '.82rem' }} onClick={() => window.location.href = '/student/subjects'}><span className="sd-btn-learn-ring" />📚 {t.quick_learn}</button>
        <button className="sd-btn sd-btn-quiz" style={{ padding: '10px 8px', fontSize: '.82rem' }} onClick={() => window.location.href = '/student/quizzes'}>❓ {t.quick_quiz}</button>
        <button className="sd-btn sd-btn-flash" style={{ padding: '10px 8px', fontSize: '.82rem' }} onClick={() => window.location.href = '/student/flashcards'}>🃏 {t.quick_flash}</button>
      </div>

      {activeChallenges.length > 0 && (
        <ChallengeCard
          challenge={activeChallenges[0]}
          lang={lang}
          t={t}
          onStart={() => {
            const ch = activeChallenges[0];
            if (!ch) return;
            const sd = JSON.parse(localStorage.getItem('student_data') || '{}');
            if (ch.unit_id && ch.unit_name && ch.subject_name) {
              window.location.href =
                `/student/subject-units?` +
                `subject_id=${ch.subject_id}` +
                `&subject=${encodeURIComponent(ch.subject_name)}` +
                `&unit_id=${ch.unit_id}` +
                `&unit=${encodeURIComponent(ch.unit_name)}` +
                `&topic=${encodeURIComponent(ch.weakness_name || '')}`;
            } else {
              window.location.href = '/student/subjects';
            }
          }}
        />
      )}

      {/* Subjects grid: 2 col, session count, needs-review badge */}
      <div className="sd-section-title">{t.subjects}</div>
      {subjects.length === 0
        ? <div className="sd-empty"><div className="sd-empty-icon">📚</div>{t.no_subjects}</div>
        : <div className="sd-subjects-grid">
            {subjects.map((s, i) => {
              const pct = Math.round(s.progress_pct ?? s.avg_correct_percentage ?? 0);
              const name = s.name || s.subject_name || '';
              const sessions = s.session_count ?? s.total_sessions ?? 0;
              return (
                <div key={s.subject_id || i} className="sd-subject-card-grid"
                  data-anim="scaleIn" data-delay={i * 60}
                  onMouseMove={e=>{const r=e.currentTarget.getBoundingClientRect();const x=(e.clientX-r.left)/r.width*100;const y=(e.clientY-r.top)/r.height*100;e.currentTarget.style.setProperty('--lx',x+'%');e.currentTarget.style.setProperty('--ly',y+'%');const rx=(e.clientY-r.top-r.height/2)/(r.height/2)*8;const ry=-(e.clientX-r.left-r.width/2)/(r.width/2)*8;e.currentTarget.style.transform=`perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg) translateZ(6px)`;e.currentTarget.classList.add('tilting');}}
                  onMouseLeave={e=>{e.currentTarget.style.transform='';e.currentTarget.classList.remove('tilting');}}
                >
                  <div className="sd-subject-shine" />
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
                    <div className={`sd-subject-ico-bg ${subjectClass(name)}`}>
                      <span className="sd-subject-emoji">{emojiFor(name)}</span>
                    </div>
                    {pct < 50 && (
                      <span style={{ fontSize: '.65rem', fontWeight: 700, background: '#FEE2E2', color: '#991B1B', borderRadius: 100, padding: '2px 7px' }}>
                        {t.needs_review_badge}
                      </span>
                    )}
                  </div>
                  <div className="sd-subject-name">{subjectLabel(name, lang)}</div>
                  <div style={{ fontSize: '.72rem', color: '#9CA3AF', fontWeight: 600, marginBottom: 6 }}>
                    {sessions} {t.sessions}
                  </div>
                  <ProgressBar pct={pct} />
                  <div className="sd-progress-pct" style={{ marginTop: 4 }}>{pct}%</div>
                  <div className="sd-subject-arrow">{lang === 'ar' ? '←' : '→'}</div>
                </div>
              );
            })}
          </div>
      }
    </>
  );
}

function Stage3({ data, lang, t }) {
  const { student, stats, subjects, active_challenge } = data;
  const avgPerf = stats.avg_performance != null
    ? Math.round(stats.avg_performance)
    : Math.round(subjects.length ? subjects.reduce((a, s) => a + (s.progress_pct ?? s.avg_correct_percentage ?? 0), 0) / subjects.length : 0);
  const focusCount = subjects.filter(s => Math.round(s.progress_pct ?? s.avg_correct_percentage ?? 0) < 50).length;

  function gradeBand(pct) {
    if (pct >= 90) return 'A*';
    if (pct >= 80) return 'A';
    if (pct >= 70) return 'B';
    if (pct >= 60) return 'C';
    return 'F';
  }

  return (
    <>
      {/* Welcome card - white, professional, no avatar */}
      <div className="sd-card" data-anim="flipIn" data-delay="0" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: 34, color: '#1E3A5F' }}>
            {t.hello} {student.first_name}
          </div>
          <div style={{ fontSize: 14, color: '#9CA3AF', fontWeight: 600, marginTop: 3 }}>
            {t.grade_label} {student.grade} — {t.dashboard_title}
          </div>
        </div>
        <button className="sd-settings-btn-dark" onClick={() => window.location.href = '/student/settings'}>{t.settings}</button>
      </div>

      {/* Active challenge / exam countdown card */}
      {active_challenge && (
        <div style={{ background: 'linear-gradient(135deg,#1E3A5F,#2D5A8E)', borderRadius: 18, padding: '18px 20px', marginBottom: 16, boxShadow: '0 4px 16px rgba(30,58,95,.3)' }}>
          <div style={{ fontSize: '.72rem', fontWeight: 800, letterSpacing: '.1em', color: 'rgba(255,255,255,.7)', textTransform: 'uppercase', marginBottom: 6 }}>
            {t.active_challenge}
          </div>
          <div style={{ fontFamily: 'Sora,sans-serif', fontWeight: 700, color: 'white', fontSize: '1rem', marginBottom: 12 }}>
            {active_challenge.title}
          </div>
          <div style={{ height: 6, background: 'rgba(255,255,255,.2)', borderRadius: 99, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: active_challenge.phase === 'phase3' ? '90%' : active_challenge.phase === 'phase2' ? '60%' : '30%', background: '#10B981', borderRadius: 99 }} />
          </div>
        </div>
      )}

      {/* Stats row: avg %, predicted grade, focus count */}
      <div className="sd-stats-row" data-anim="fadeUp" data-delay="80">
        <div className="sd-stat-card sd-stat-green">
          <div className="sd-stat-glow" />
          <span className="sd-stat-ico">📊</span>
          <div className="sd-stat-val" style={{ color: barColor(avgPerf) }}>{avgPerf}%</div>
          <div className="sd-stat-lbl">{t.avg}</div>
        </div>
        <div className="sd-stat-card sd-stat-blue">
          <div className="sd-stat-glow" />
          <div className="sd-stat-val" style={{ color: '#1E3A5F' }}>{gradeBand(avgPerf)}</div>
          <div className="sd-stat-lbl">{t.predicted}</div>
        </div>
        <div className={`sd-stat-card ${focusCount > 0 ? 'sd-stat-red' : 'sd-stat-amber'}`}>
          <div className="sd-stat-glow" />
          <div className="sd-stat-val" style={{ color: focusCount > 0 ? '#DC2626' : '#059669' }}>{focusCount}</div>
          <div className="sd-stat-lbl">{t.focus_count}</div>
        </div>
      </div>

      {/* Quick actions */}
      <div data-anim="fadeUp" data-delay="140" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 18 }}>
        <button className="sd-btn sd-btn-learn" style={{ padding: '10px 8px', fontSize: '.82rem' }} onClick={() => window.location.href = '/student/subjects'}><span className="sd-btn-learn-ring" />📚 {t.quick_learn}</button>
        <button className="sd-btn sd-btn-quiz" style={{ padding: '10px 8px', fontSize: '.82rem' }} onClick={() => window.location.href = '/student/quizzes'}>❓ {t.quick_quiz}</button>
        <button className="sd-btn sd-btn-flash" style={{ padding: '10px 8px', fontSize: '.82rem' }} onClick={() => window.location.href = '/student/flashcards'}>🃏 {t.quick_flash}</button>
      </div>

      {/* Subjects: full-width rows, grade band, needs attention */}
      <div className="sd-section-title">{t.subjects}</div>
      {subjects.length === 0
        ? <div className="sd-empty"><div className="sd-empty-icon">📚</div>{t.no_subjects}</div>
        : <div className="sd-subjects-list">
            {subjects.map((s, i) => {
              const pct = Math.round(s.progress_pct ?? s.avg_correct_percentage ?? 0);
              const name = s.name || s.subject_name || '';
              const band = gradeBand(pct);
              const bandColor = pct >= 80 ? '#059669' : pct >= 60 ? '#D97706' : '#EF4444';
              return (
                <div key={s.subject_id || i} className="sd-subject-row" data-anim="scaleIn" data-delay={i * 60}>
                  <div className="sd-subject-shine" />
                  <div className={`sd-subject-ico-bg ${subjectClass(name)}`} style={{ flexShrink: 0 }}>
                    <span className="sd-subject-emoji">{emojiFor(name)}</span>
                  </div>
                  <div className="sd-subject-row-info">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <div className="sd-subject-row-name" style={{ marginBottom: 0 }}>{subjectLabel(name, lang)}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontFamily: 'Sora,sans-serif', fontSize: '.92rem', fontWeight: 800, color: bandColor }}>{band}</span>
                        <span className="sd-subject-row-pct" style={{ color: barColor(pct) }}>{pct}%</span>
                      </div>
                    </div>
                    <ProgressBar pct={pct} />
                    {pct < 50 && (
                      <div style={{ fontSize: '.72rem', fontWeight: 700, color: '#EF4444', marginTop: 4 }}>{t.needs_attention}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
      }
    </>
  );
}

export default function StudentDashboard() {
  const [data,             setData]             = useState(null);
  const [loading,          setLoading]          = useState(true);
  const [error,            setError]            = useState(null);
  const [activeChallenges, setActiveChallenges] = useState([]);
  const [challengesDone,   setChallengesDone]   = useState(0);

  const { lang: ctxLang } = useLanguage();
  const sd   = (() => { try { return JSON.parse(localStorage.getItem('student_data') || '{}'); } catch { return {}; } })();
  const lang = sd.language || ctxLang;
  const dir  = lang === 'ar' ? 'rtl' : 'ltr';
  const t    = T[lang];

  const fetchDashboard = async () => {
    const token     = localStorage.getItem('student_token');
    const studentId = sd.id;
    if (!token || !studentId) { window.location.href = '/student/auth'; return; }
    const DASH_CACHE_KEY = `jahbath_dashboard_${studentId}`;
    const DASH_CACHE_TTL = 5 * 60 * 1000;
    const cachedDash = localStorage.getItem(DASH_CACHE_KEY);
    const cachedDashTime = localStorage.getItem(DASH_CACHE_KEY + '_time');
    if (cachedDash && cachedDashTime && Date.now() - Number(cachedDashTime) < DASH_CACHE_TTL) {
      setData(JSON.parse(cachedDash));
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await studentFetch(`${API}/student/dashboard/${studentId}`);
      if (!res) return;
      const d = await res.json();
      if (d.success) {
        setData(d);
        localStorage.setItem(DASH_CACHE_KEY, JSON.stringify(d));
        localStorage.setItem(DASH_CACHE_KEY + '_time', Date.now().toString());
      } else {
        setError(d.error || t.error_msg);
      }
    } catch {
      setError(t.error_msg);
    } finally {
      setLoading(false);
    }
  };

  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 100);
    camera.position.z = 7;
    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const L1 = new THREE.DirectionalLight(0xffffff, 1.2); L1.position.set(6, 8, 4); scene.add(L1);
    const L2 = new THREE.DirectionalLight(0xffffff, 0.6); L2.position.set(-5, -3, 4); scene.add(L2);
    const meshes = [];
    [
      [new THREE.IcosahedronGeometry(0.36, 0), -4, 2.5, -1.2],
      [new THREE.IcosahedronGeometry(0.24, 0), 3.6, 2, -2],
      [new THREE.OctahedronGeometry(0.30, 0), -3, -1.8, -1.5],
      [new THREE.OctahedronGeometry(0.20, 0), 2.8, -2.2, -1],
      [new THREE.TetrahedronGeometry(0.26, 0), 0.7, 2.8, -1.8],
      [new THREE.TetrahedronGeometry(0.16, 0), -1.4, -2.6, -1.4],
      [new THREE.IcosahedronGeometry(0.14, 0), 4, 0.5, -0.6],
      [new THREE.OctahedronGeometry(0.12, 0), -4, -0.7, -0.6],
      [new THREE.IcosahedronGeometry(0.11, 0), 1.8, -3, -0.9],
      [new THREE.TetrahedronGeometry(0.13, 0), -2, 3, -0.9],
    ].forEach(([G, x, y, z]) => {
      const mat = new THREE.MeshPhongMaterial({ color: 0x34D399, transparent: true, opacity: 0.22, shininess: 120, specular: new THREE.Color(0xffffff) });
      const m = new THREE.Mesh(G, mat);
      m.position.set(x, y, z);
      m.rotation.set(Math.random() * Math.PI * 2, Math.random() * Math.PI * 2, Math.random() * Math.PI * 2);
      m._oy = y; m._fs = 0.2 + Math.random() * 0.5; m._fa = 0.12 + Math.random() * 0.22;
      m._rx = (0.002 + Math.random() * 0.006) * (Math.random() > 0.5 ? 1 : -1);
      m._ry = (0.002 + Math.random() * 0.005) * (Math.random() > 0.5 ? 1 : -1);
      m._ph = Math.random() * Math.PI * 2;
      scene.add(m); meshes.push(m);
    });
    const PC = 120;
    const pp = new Float32Array(PC * 3);
    const pv = [];
    for (let i = 0; i < PC; i++) {
      pp[i * 3] = (Math.random() - 0.5) * 16;
      pp[i * 3 + 1] = (Math.random() - 0.5) * 12;
      pp[i * 3 + 2] = (Math.random() - 0.5) * 5 - 1.5;
      pv.push({ vx: (Math.random() - 0.5) * 0.007, vy: (Math.random() - 0.5) * 0.007 });
    }
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute('position', new THREE.BufferAttribute(pp, 3));
    const pMat = new THREE.PointsMaterial({ color: 0x34D399, size: 0.07, transparent: true, opacity: 0.5, sizeAttenuation: true });
    scene.add(new THREE.Points(pGeo, pMat));
    const lp = new Float32Array(PC * PC * 6);
    const lGeo = new THREE.BufferGeometry();
    lGeo.setAttribute('position', new THREE.BufferAttribute(lp, 3));
    const lMesh = new THREE.LineSegments(lGeo, new THREE.LineBasicMaterial({ color: 0x34D399, transparent: true, opacity: 0.1 }));
    scene.add(lMesh);
    let mx = 0, my = 0;
    const onMM = (e) => { mx = (e.clientX / window.innerWidth - 0.5) * 2; my = (e.clientY / window.innerHeight - 0.5) * 2; };
    window.addEventListener('mousemove', onMM);
    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', onResize);
    let t = 0, li = 0, animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      t += 0.007;
      meshes.forEach(m => {
        m.rotation.x += m._rx; m.rotation.y += m._ry;
        m.position.y = m._oy + Math.sin(t * m._fs + m._ph) * m._fa;
      });
      for (let i = 0; i < PC; i++) {
        pp[i * 3] += pv[i].vx; pp[i * 3 + 1] += pv[i].vy;
        if (Math.abs(pp[i * 3]) > 8) pv[i].vx *= -1;
        if (Math.abs(pp[i * 3 + 1]) > 6) pv[i].vy *= -1;
      }
      pGeo.attributes.position.needsUpdate = true;
      li = 0;
      for (let i = 0; i < PC; i++) for (let j = i + 1; j < PC; j++) {
        const dx = pp[i * 3] - pp[j * 3], dy = pp[i * 3 + 1] - pp[j * 3 + 1];
        if (dx * dx + dy * dy < 5) {
          lp[li++] = pp[i * 3]; lp[li++] = pp[i * 3 + 1]; lp[li++] = pp[i * 3 + 2];
          lp[li++] = pp[j * 3]; lp[li++] = pp[j * 3 + 1]; lp[li++] = pp[j * 3 + 2];
        }
      }
      lGeo.attributes.position.needsUpdate = true;
      lGeo.setDrawRange(0, li / 3);
      camera.position.x += (mx * 0.3 - camera.position.x) * 0.032;
      camera.position.y += (-my * 0.18 - camera.position.y) * 0.032;
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
    };
    animate();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('mousemove', onMM);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
    };
  }, []);

  useEffect(() => {
    if (loading || !data) return;
    const els = document.querySelectorAll('[data-anim]');
    els.forEach(el => el.classList.remove('in'));
    const timers = [];
    els.forEach((el, i) => {
      const delay = el.dataset.delay ? +el.dataset.delay : i * 60;
      timers.push(setTimeout(() => el.classList.add('in'), delay + 100));
    });
    return () => timers.forEach(clearTimeout);
  }, [loading, data]);

  useEffect(() => { fetchDashboard(); }, []);

  useEffect(() => {
    const token     = localStorage.getItem('student_token');
    const studentId = sd.id;
    if (!token || !studentId) return;
    studentFetch(`${API}/student/challenges/${studentId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r ? r.json() : null)
      .then(d => {
        if (!d) return;
        if (d.success) {
          setActiveChallenges(d.active || []);
          setChallengesDone(d.total_completed || 0);
        }
      })
      .catch(() => {});
  }, [sd.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const grade = data?.student?.grade || sd.grade || 1;
  const stage = getStage(grade);

  const sidebarStyle = {
    [dir === 'rtl' ? 'right' : 'left']: 0,
    boxShadow: dir === 'rtl' ? '-2px 0 12px rgba(5,150,105,.06)' : '2px 0 12px rgba(5,150,105,.06)',
    ...(lang !== 'ar' && { borderRight: '1px solid #E5E7EB', borderLeft: 'none' }),
  };

  const navItems = [
    { icon: '🏠', label: t.nav_home,       href: '/student/dashboard', active: true },
    { icon: '📚', label: t.nav_learn,      href: '/student/subjects'  },
    { icon: '❓', label: t.nav_quiz,       href: '/student/quizzes'  },
    { icon: '📊', label: t.nav_results,    href: '/student/results'   },
    { icon: '🃏', label: t.nav_flash,      href: '/student/flashcards'},
    { icon: '🎯', label: t.nav_challenges, href: '/student/challenges', badge: activeChallenges.length || 0 },
    { icon: '🏆', label: t.nav_achieve,    href: '/student/achievements'},
  ];

  const mainMargin = {
    [dir === 'rtl' ? 'marginRight' : 'marginLeft']:
      typeof window !== 'undefined' && window.innerWidth >= 1024 ? 220 :
      typeof window !== 'undefined' && window.innerWidth >= 768  ? 200 : 0,
  };

  return (
    <div className="sd-root" style={{ direction: dir }} dir={dir}>
      <canvas ref={canvasRef} style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }} />
      <style>{CSS}</style>

      <nav className="sd-sidebar" style={sidebarStyle}>
        <div className="sd-sidebar-logo">Jah<span>bath</span></div>
        <div className="sd-nav">
          {navItems.map(item => (
            <button
              key={item.label}
              className={`sd-nav-item${item.active ? ' active' : ''}`}
              onClick={() => window.location.href = item.href}
              style={{ textAlign: lang === 'ar' ? 'right' : 'left' }}
            >
              <span className="icon">{item.icon}</span>
              {item.label}
              {item.badge > 0 && (
                <span style={{
                  background: '#EF4444',
                  color: 'white',
                  borderRadius: '50%',
                  fontSize: 11,
                  fontWeight: 700,
                  minWidth: 18,
                  height: 18,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginLeft: 6,
                  padding: '0 4px',
                }}>
                  {item.badge}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="sd-sidebar-footer">
          <button className="sd-logout-btn" onClick={async () => {
            await logoutAndClearLocal('student');
            window.location.href = '/student/auth';
          }}>
            🚪 {t.logout}
          </button>
        </div>
      </nav>

      <main className="sd-main" style={mainMargin}>
        {loading && <LoadingSkeleton />}

        {!loading && error && (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>⚠️</div>
            <div style={{ fontFamily: 'Sora,sans-serif', fontWeight: 700, color: '#064E3B', marginBottom: 16 }}>{error}</div>
            <button className="sd-btn sd-btn-green" onClick={fetchDashboard}>{t.retry}</button>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {stage === 1 && <Stage1 data={data} lang={lang} t={t} activeChallenges={activeChallenges} challengesDone={challengesDone} />}
            {stage === 2 && <Stage2 data={data} lang={lang} t={t} activeChallenges={activeChallenges} challengesDone={challengesDone} />}
            {stage === 3 && <Stage3 data={data} lang={lang} t={t} />}
          </>
        )}
      </main>

      <nav className="sd-bottom-nav">
        <div className="sd-bottom-nav-inner">
          {navItems.slice(0, 5).map(item => (
            <button
              key={item.label}
              className={`sd-bottom-item${item.active ? ' active' : ''}`}
              onClick={() => window.location.href = item.href}
            >
              <span className="bi">{item.icon}</span>
              {item.label}
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}
